import L from 'leaflet'
import { type Component, createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, splitProps } from 'solid-js'
import 'leaflet/dist/leaflet.css'
import './Map.css'
import { twMerge } from 'tailwind-merge'
import { useAppState } from '~/contexts/ThemeContext'
import { api } from '~/services/api'
import type { MapLayerOptionType, MapPropsType, MapThemeType, ReverseGeocodeResultType } from '~/types/Map'

// biome-ignore lint/suspicious/noExplicitAny: Leaflet internals
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const DEFAULT_LAYERS: MapLayerOptionType[] = [
  {
    id: 'google',
    label: 'Google Maps',
    url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    attribution: '&copy; Google',
    invertOnDark: true,
    icon: 'M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z'
  },
  {
    id: 'light',
    label: 'Estándar',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap',
    invertOnDark: true,
    icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
  },
  {
    id: 'dark',
    label: 'Oscuro',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; CARTO',
    icon: 'M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z'
  },
  {
    id: 'satellite',
    label: 'Satélite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri',
    icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z'
  },
  {
    id: 'terrain',
    label: 'Terreno',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenTopoMap',
    invertOnDark: true,
    maxNativeZoom: 17,
    icon: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z'
  }
]

const btnClass =
  'flex size-8 items-center justify-center rounded-lg bg-white/90 text-gray-700 shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-white hover:shadow-lg hover:scale-105 active:scale-95 dark:bg-gray-800/90 dark:text-gray-200 dark:hover:bg-gray-800'
const btnActiveClass =
  'flex size-8 items-center justify-center rounded-lg bg-primary text-white shadow-md shadow-primary/30 transition-all duration-200 hover:bg-primary/90 hover:shadow-lg hover:scale-105 active:scale-95'

function makeCheckSvg(color: string, style: 'check' | 'dot' | 'filled') {
  if (style === 'dot') return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><circle cx="12" cy="12" r="5" fill="${color}"/></svg>`
  if (style === 'filled') return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><circle cx="12" cy="12" r="8" fill="${color}"/></svg>`
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="${color}"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>`
}

function makeIcon(color?: string, customIcon?: string): L.Icon {
  if (customIcon) return L.icon({ iconUrl: customIcon, iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34] })
  if (color) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${color}"/><circle cx="12" cy="12" r="5" fill="white"/></svg>`
    return L.icon({ iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`, iconSize: [24, 36], iconAnchor: [12, 36], popupAnchor: [0, -36] })
  }
  return L.icon({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  })
}

const MapView: Component<MapPropsType> = rawProps => {
  const [local] = splitProps(rawProps, [
    'center',
    'zoom',
    'markers',
    'polylines',
    'circles',
    'polygons',
    'height',
    'class',
    'theme',
    'tileLayer',
    'layers',
    'controlStyle',
    'interactive',
    'zoomControl',
    'layerControl',
    'fullscreenControl',
    'geolocationControl',
    'measureControl',
    'scaleControl',
    'attributionControl',
    'fitBounds',
    'onMapClick',
    'onMarkerClick',
    'onPolylineClick',
    'onCircleClick',
    'onPolygonClick',
    'onLayerChange',
    'onGeolocate',
    'onMeasure',
    'onReady',
    'onReverseGeocode',
    'reverseGeocode',
    'reverseGeocodeProvider',
    'defaultMarkerIcon',
    'mapboxToken',
    'googleApiKey'
  ])

  let containerRef: HTMLDivElement | undefined
  let leafletMap: L.Map | undefined
  let markersGroup: L.LayerGroup | undefined
  let shapesGroup: L.LayerGroup | undefined
  let tileLayerRef: L.TileLayer | undefined
  let geolocateCircle: L.Circle | undefined
  let measurePoints: Array<[number, number]> = []
  let measureLine: L.Polyline | undefined
  let measureMarkers: L.Layer[] = []
  let currentLayerUrl = ''

  const { isDark } = useAppState()

  // El mapa llega hasta z19 aunque el proveedor no publique tiles a ese nivel
  // (OpenTopoMap se queda en 17). Con maxNativeZoom Leaflet escala los del último
  // nivel disponible en vez de pedir tiles inexistentes y quedarse en blanco.
  const createTileLayer = (layer: MapLayerOptionType) => L.tileLayer(layer.url, { attribution: layer.attribution ?? '', maxZoom: 19, maxNativeZoom: layer.maxNativeZoom ?? 19 })

  // Las capas claras (Google, OpenStreetMap) no tienen variante oscura: en modo
  // oscuro se invierten sus colores y se rota el tono para que el agua y la
  // vegetación no queden en su color complementario.
  const DARK_INVERT_FILTER = 'invert(1) hue-rotate(180deg) brightness(0.95) contrast(0.9)'
  const applyPaneFilter = () => {
    if (!leafletMap || !containerRef) return
    const pane = containerRef.querySelector('.leaflet-tile-pane') as HTMLElement | null
    if (!pane) return
    const inverted = isDark() && Boolean(activeLayer().invertOnDark)
    pane.style.filter = inverted ? DARK_INVERT_FILTER : ''
    // El blanco lo invierte el propio filtro y queda negro, el color del mapa ya
    // oscurecido: así la costura entre tiles no se distingue del mapa.
    pane.style.backgroundColor = inverted ? '#fff' : ''
  }

  const [layersOpen, setLayersOpen] = createSignal(false)
  const [activeLayerId, setActiveLayerId] = createSignal<MapThemeType | string>(local.theme ?? 'google')
  const [isFullscreen, setIsFullscreen] = createSignal(false)
  const [isMeasuring, setIsMeasuring] = createSignal(false)
  const [isLocating, setIsLocating] = createSignal(false)
  const [showDragHint, setShowDragHint] = createSignal(false)

  const mapId = `map-${Math.random().toString(36).slice(2, 9)}`

  const availableLayers = createMemo(() => {
    const base = local.layers?.length ? local.layers : DEFAULT_LAYERS
    if (local.mapboxToken) {
      return [
        ...base,
        {
          id: 'mapbox',
          label: 'Mapbox',
          url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${local.mapboxToken}`,
          attribution: '&copy; Mapbox',
          icon: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5'
        }
      ]
    }
    if (local.googleApiKey) {
      return [
        ...base,
        {
          id: 'google',
          label: 'Google Maps',
          url: `https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}&key=${local.googleApiKey}`,
          attribution: '&copy; Google',
          invertOnDark: true,
          icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
        }
      ]
    }
    return base
  })

  const activeLayer = createMemo(() => availableLayers().find(l => l.id === activeLayerId()) ?? availableLayers()[0])
  const center = createMemo<[number, number]>(() => local.center ?? [4.711, -74.072])
  const zoom = createMemo(() => local.zoom ?? 12)

  const renderMarkers = () => {
    if (!leafletMap || !local.markers?.length) return
    if (markersGroup) leafletMap.removeLayer(markersGroup)
    markersGroup = L.layerGroup().addTo(leafletMap)
    for (let i = 0; i < local.markers.length; i++) {
      const m = local.markers[i]
      const marker = L.marker(m.position, { icon: makeIcon(m.color, local.defaultMarkerIcon) }).addTo(markersGroup)
      if (m.title) marker.bindTooltip(m.title, { direction: 'top', offset: [0, -36] })
      if (m.popup) marker.bindPopup(String(m.popup))
      marker.on('click', () => local.onMarkerClick?.(m, i))
    }
    if (local.fitBounds && local.markers.length > 0) {
      leafletMap.fitBounds(L.latLngBounds(local.markers.map(m => m.position)), { padding: [50, 50] })
    }
  }

  const renderShapes = () => {
    if (!leafletMap) return
    if (shapesGroup) leafletMap.removeLayer(shapesGroup)
    shapesGroup = L.layerGroup().addTo(leafletMap)

    local.polylines?.forEach((pl, i) => {
      const line = L.polyline(pl.points, {
        color: pl.color ?? '#3b82f6',
        weight: pl.weight ?? 3,
        opacity: pl.opacity ?? 1,
        dashArray: pl.dashArray,
        lineCap: pl.lineCap ?? 'round',
        lineJoin: pl.lineJoin ?? 'round'
      }).addTo(shapesGroup!)
      line.on('click', () => local.onPolylineClick?.(pl, i))
    })

    local.circles?.forEach((c, i) => {
      const circle = L.circle(c.center, {
        radius: c.radius,
        color: c.color ?? '#3b82f6',
        fillColor: c.fillColor ?? '#3b82f6',
        fillOpacity: c.fillOpacity ?? 0.2,
        weight: c.weight ?? 2,
        dashArray: c.dashArray
      }).addTo(shapesGroup!)
      circle.on('click', () => local.onCircleClick?.(c, i))
    })

    local.polygons?.forEach((p, i) => {
      const polygon = L.polygon(p.points, {
        color: p.color ?? '#3b82f6',
        fillColor: p.fillColor ?? '#3b82f6',
        fillOpacity: p.fillOpacity ?? 0.2,
        weight: p.weight ?? 2,
        dashArray: p.dashArray,
        lineJoin: p.lineJoin ?? 'round'
      }).addTo(shapesGroup!)
      polygon.on('click', () => local.onPolygonClick?.(p, i))
    })
  }

  const switchLayer = (layer: MapLayerOptionType) => {
    setActiveLayerId(layer.id)
    setLayersOpen(false)
    local.onLayerChange?.(layer)
  }

  const handleFullscreenChange = () => {
    setIsFullscreen(Boolean(document.fullscreenElement))
    window.setTimeout(() => leafletMap?.invalidateSize(), 150)
  }

  const toggleFullscreen = () => {
    if (!containerRef) return
    if (document.fullscreenElement) {
      void document.exitFullscreen?.()
    } else {
      void containerRef.requestFullscreen?.()
    }
  }

  /**
   * En móvil, arrastrar el dedo sobre el mapa lo desplazaba en vez de hacer
   * scroll de la página: Leaflet cancela el gesto con `preventDefault` y no
   * había forma de pasar de largo. Con el arrastre apagado Leaflet no toca el
   * gesto de un dedo y el navegador desplaza la página con normalidad; el
   * pellizco de dos dedos sigue acercando y reencuadrando el mapa. En pantalla
   * completa no hay página que desplazar, así que ahí un dedo arrastra como
   * siempre.
   */
  const isCoarsePointer = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches)
  const activePointers = new Set<number>()
  let hintTimer: number | undefined

  const applyTouchDrag = () => {
    if (!leafletMap || !isCoarsePointer || !(local.interactive ?? true)) return
    if (isFullscreen()) leafletMap.dragging.enable()
    else leafletMap.dragging.disable()
  }

  const handlePointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'touch') activePointers.add(e.pointerId)
  }

  const handlePointerUp = (e: PointerEvent) => {
    activePointers.delete(e.pointerId)
  }

  // Sin el aviso, arrastrar con un dedo no haría nada visible sobre el mapa y
  // parecería que se ha quedado muerto.
  const handlePointerMove = (e: PointerEvent) => {
    if (e.pointerType !== 'touch' || isFullscreen() || activePointers.size !== 1) return
    setShowDragHint(true)
    window.clearTimeout(hintTimer)
    hintTimer = window.setTimeout(() => setShowDragHint(false), 1800)
  }

  const geolocate = () => {
    if (!leafletMap || !navigator.geolocation) return
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const { latitude: lat, longitude: lng } = pos.coords
        leafletMap!.flyTo([lat, lng], 15, { duration: 1.5 })
        if (geolocateCircle) leafletMap!.removeLayer(geolocateCircle)
        geolocateCircle = L.circle([lat, lng], { radius: 50, color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2, weight: 2 }).addTo(leafletMap!)
        local.onGeolocate?.({ lat, lng })
        if (local.reverseGeocode && local.onReverseGeocode) {
          const result = await doReverseGeocode(lat, lng)
          local.onReverseGeocode(result)
        }
        setIsLocating(false)
      },
      () => setIsLocating(false),
      { enableHighAccuracy: true }
    )
  }

  const startMeasure = () => {
    setIsMeasuring(true)
    measurePoints = []
    if (measureLine && leafletMap) leafletMap.removeLayer(measureLine)
    measureMarkers.forEach(m => leafletMap?.removeLayer(m))
    measureMarkers = []
  }

  const stopMeasure = () => {
    setIsMeasuring(false)
    measurePoints = []
    if (measureLine && leafletMap) leafletMap.removeLayer(measureLine)
    measureMarkers.forEach(m => leafletMap?.removeLayer(m))
    measureMarkers = []
  }

  const handleMeasureClick = (latlng: { lat: number; lng: number }) => {
    if (!isMeasuring() || !leafletMap) return
    measurePoints.push([latlng.lat, latlng.lng])
    const marker = L.circleMarker([latlng.lat, latlng.lng], { radius: 4, color: '#ef4444', fillColor: '#ef4444', fillOpacity: 1, weight: 2 }).addTo(leafletMap)
    measureMarkers.push(marker)
    if (measureLine) leafletMap.removeLayer(measureLine)
    if (measurePoints.length >= 2) {
      measureLine = L.polyline(measurePoints, { color: '#ef4444', weight: 2, dashArray: '6 6' }).addTo(leafletMap)
      let total = 0
      for (let i = 1; i < measurePoints.length; i++) {
        total += leafletMap.distance(measurePoints[i - 1], measurePoints[i])
      }
      local.onMeasure?.(total, [...measurePoints])
    }
  }

  /**
   * Geocodificación inversa: coordenadas → dirección.
   * Usa Nominatim (OpenStreetMap), gratuito y sin API key.
   */
  const reverseGeocode = async (lat: number, lng: number): Promise<ReverseGeocodeResultType> => {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`
    try {
      const res = await fetch(url, { headers: { 'Accept-Language': 'es' } })
      if (!res.ok) return { lat, lng }
      const data = await res.json()
      const addr = data.address ?? {}

      // Componentes específicos de dirección (ignorar suburb, neighbourhood, etc.)
      const road = addr.road || addr.pedestrian || addr.footway
      const houseNumber = addr.house_number
      const city = addr.city || addr.town || addr.village || addr.municipality
      const state = addr.state || addr.region
      const postalCode = addr.postcode

      // Construir dirección limpia: solo calle + número + ciudad
      let address = ''
      if (road) {
        // Formato colombiano: "Tv. 72 # 20-60" o "Calle 123 45-67"
        address = houseNumber ? `${road} # ${houseNumber}` : road
        if (city) address += `, ${city}`
      } else {
        // Fallback: usar display_name pero truncado
        const parts = (data.display_name || '').split(',').slice(0, 3).join(',').trim()
        address = parts || data.display_name
      }

      return {
        lat,
        lng,
        address,
        postalCode,
        country: addr.country,
        city,
        state,
        raw: addr
      }
    } catch {
      return { lat, lng }
    }
  }

  /**
   * Geocodificación inversa con Google Maps API (vía backend proxy).
   * Más preciso que Nominatim, especialmente en Colombia.
   */
  const reverseGeocodeGoogle = async (lat: number, lng: number): Promise<ReverseGeocodeResultType> => {
    try {
      const { data } = await api.get('/geo/reverse-geocode', { params: { lat, lng } })
      const result = data.data

      return {
        lat,
        lng,
        address: result.address,
        postalCode: result.postalCode,
        country: result.country,
        city: result.city,
        state: result.state,
        raw: {
          ...result,
          neighborhood: result.neighborhood,
          street: result.street,
          streetNumber: result.streetNumber
        }
      }
    } catch {
      return await reverseGeocode(lat, lng) // Fallback a Nominatim
    }
  }

  /**
   * Elige el provider de geocodificación según la prop reverseGeocodeProvider.
   */
  const doReverseGeocode = (lat: number, lng: number): Promise<ReverseGeocodeResultType> => {
    return local.reverseGeocodeProvider === 'google' ? reverseGeocodeGoogle(lat, lng) : reverseGeocode(lat, lng)
  }

  onMount(() => {
    if (!containerRef) return

    /**
     * El mapa se inicializa sobre el único hijo del contenedor (el div que
     * lleva `mapId`). Dentro del portal de un Modal el componente puede montar
     * antes de que ese nodo esté conectado al documento: `L.map` lanzaría
     * "Map container not found" y ese error sin capturar en el root del portal
     * derriba la aplicación. Si aún no está conectado se reintenta en el
     * siguiente frame; si para entonces el componente se desmontó (modal
     * cerrado), el cleanup cancela el intento y un montaje nuevo lo repite.
     */
    const initMap = (): boolean => {
      const target = containerRef?.firstElementChild as HTMLElement | undefined
      if (!target?.isConnected) return false
      leafletMap = L.map(target, {
        center: center(),
        zoom: zoom(),
        zoomControl: false,
        attributionControl: local.attributionControl ?? false,
        scrollWheelZoom: local.interactive ?? true,
        dragging: local.interactive ?? true,
        touchZoom: local.interactive ?? true,
        doubleClickZoom: local.interactive ?? true,
        boxZoom: false,
        keyboard: false
      })
      leafletMap.on('zoomend', () => {})
      leafletMap.on('click', async e => {
        const ll = { lat: e.latlng.lat, lng: e.latlng.lng }
        if (isMeasuring()) handleMeasureClick(ll)
        else {
          local.onMapClick?.(ll)
          if (local.reverseGeocode && local.onReverseGeocode) {
            const result = await doReverseGeocode(ll.lat, ll.lng)
            local.onReverseGeocode(result)
          }
        }
      })
      tileLayerRef = createTileLayer(activeLayer()).addTo(leafletMap)
      currentLayerUrl = activeLayer().url
      applyPaneFilter()
      if (local.scaleControl) leafletMap.addControl(L.control.scale({ imperial: false, position: 'bottomleft' }))
      renderMarkers()
      renderShapes()
      local.onReady?.(leafletMap)
      document.addEventListener('fullscreenchange', handleFullscreenChange)
      if (isCoarsePointer) {
        applyTouchDrag()
        target.addEventListener('pointerdown', handlePointerDown, { passive: true })
        target.addEventListener('pointermove', handlePointerMove, { passive: true })
        target.addEventListener('pointerup', handlePointerUp, { passive: true })
        target.addEventListener('pointercancel', handlePointerUp, { passive: true })
      }
      return true
    }

    if (!initMap()) {
      const rafId = requestAnimationFrame(() => {
        if (!initMap()) leafletMap = undefined
      })
      onCleanup(() => cancelAnimationFrame(rafId))
    }
  })

  createEffect(() => {
    if (leafletMap) leafletMap.setView(center(), zoom())
  })
  createEffect(() => {
    isFullscreen()
    applyTouchDrag()
  })
  createEffect(() => {
    isDark()
    if (!leafletMap) return
    const layer = activeLayer()
    if (currentLayerUrl !== layer.url) {
      if (tileLayerRef) leafletMap.removeLayer(tileLayerRef)
      tileLayerRef = createTileLayer(layer).addTo(leafletMap)
      currentLayerUrl = layer.url
    }
    applyPaneFilter()
  })
  createEffect(() => {
    local.markers
    renderMarkers()
  })
  createEffect(() => {
    local.polylines
    local.circles
    local.polygons
    renderShapes()
  })

  const handleZoom = (action: 'zoomIn' | 'zoomOut' | 'reset') => {
    if (!leafletMap) return
    if (action === 'zoomIn') leafletMap.zoomIn()
    else if (action === 'zoomOut') leafletMap.zoomOut()
    else leafletMap.setView(center(), zoom())
  }

  onCleanup(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange)
    window.clearTimeout(hintTimer)
    const target = containerRef?.firstElementChild as HTMLElement | undefined
    target?.removeEventListener('pointerdown', handlePointerDown)
    target?.removeEventListener('pointermove', handlePointerMove)
    target?.removeEventListener('pointerup', handlePointerUp)
    target?.removeEventListener('pointercancel', handlePointerUp)
    if (leafletMap) {
      leafletMap.remove()
      leafletMap = undefined
    }
  })

  const show = (prop?: boolean) => prop !== false && (local.interactive ?? true)

  return (
    <div
      ref={containerRef}
      class={twMerge('relative isolate rounded-xl overflow-hidden border border-border bg-surface', isFullscreen() && 'rounded-none border-none', local.class)}
      style={{ height: local.height ?? '400px' }}
    >
      <div id={mapId} class="size-full" />

      <Show when={showDragHint()}>
        <div class="pointer-events-none absolute inset-0 z-[1200] flex items-center justify-center bg-black/25">
          <span class="mx-6 rounded-xl bg-black/75 px-4 py-2 text-center text-sm font-medium text-white">Pellizca con dos dedos para acercar, o ábrelo a pantalla completa para moverlo</span>
        </div>
      </Show>

      <Show when={show(local.zoomControl) || show(local.layerControl) || show(local.geolocationControl) || show(local.measureControl)}>
        <div class="absolute right-3 top-3 z-1000 flex flex-col gap-1">
          <Show when={show(local.zoomControl)}>
            <For
              each={[
                { icon: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z', action: 'zoomIn' as const, tip: 'Acercar' },
                { icon: 'M19 13H5v-2h14v2z', action: 'zoomOut' as const, tip: 'Alejar' },
                {
                  icon: 'M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z',
                  action: 'reset' as const,
                  tip: 'Restablecer'
                }
              ]}
            >
              {btn => (
                <button type="button" onClick={() => handleZoom(btn.action)} class={btnClass} title={btn.tip}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d={btn.icon} />
                  </svg>
                </button>
              )}
            </For>
          </Show>

          <Show when={show(local.layerControl)}>
            <div class="relative">
              <button type="button" onClick={() => setLayersOpen(!layersOpen())} class={btnClass} title="Capas">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" />
                </svg>
              </button>
              <Show when={layersOpen()}>
                <div class="absolute right-11 top-0 w-48 rounded-2xl bg-white/95 p-1.5 shadow-xl backdrop-blur-sm dark:bg-gray-800/95">
                  <For each={availableLayers()}>
                    {layer => (
                      <button
                        type="button"
                        onClick={() => switchLayer(layer)}
                        class={twMerge(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
                          activeLayerId() === layer.id ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                        )}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" class="shrink-0 opacity-70">
                          <path d={layer.icon ?? DEFAULT_LAYERS[0].icon} />
                        </svg>
                        <span>{layer.label}</span>
                        <Show when={activeLayerId() === layer.id}>
                          <span class="ml-auto" innerHTML={makeCheckSvg(local.controlStyle?.checkColor ?? '#6366f1', local.controlStyle?.checkStyle ?? 'check')} />
                        </Show>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Show>

          <Show when={show(local.geolocationControl)}>
            <button type="button" onClick={geolocate} class={isLocating() ? btnActiveClass : btnClass} title="Mi ubicación" disabled={isLocating()}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
              </svg>
            </button>
          </Show>

          <Show when={show(local.measureControl)}>
            <button
              type="button"
              onClick={isMeasuring() ? stopMeasure : startMeasure}
              class={isMeasuring() ? btnActiveClass : btnClass}
              title={isMeasuring() ? 'Finalizar medición' : 'Medir distancia'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2v8z" />
              </svg>
            </button>
          </Show>
        </div>
      </Show>

      <Show when={show(local.fullscreenControl)}>
        <div class="absolute right-3 bottom-3 z-1000">
          <button type="button" onClick={toggleFullscreen} class={btnClass} title={isFullscreen() ? 'Salir' : 'Pantalla completa'}>
            <Show
              when={isFullscreen()}
              fallback={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                </svg>
              }
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
              </svg>
            </Show>
          </button>
        </div>
      </Show>
    </div>
  )
}

export default MapView
